import React, { useEffect } from 'react'
import { func, object, bool, string } from 'prop-types'
import { withFormik } from 'formik'
import * as Yup from 'yup'
import styled from 'styled-components'

import { minLength } from 'utils/validators'
import { trackEvent, trackQueryParam, untrackQueryParam } from 'utils/tracking'

import { Link } from 'gatsby'
import Modal, { ModalSection } from 'components/Modal'
import Input from 'components/Input'
import Button from 'components/button/Button'
import { H2, fontWeights } from 'styles/typography'
import { black } from 'styles/colors'
import Fieldset from 'components/Fieldset/index'
import FieldsetItem from 'components/Fieldset/FieldsetItem'
import FormFeedback from 'components/FormFeedback'
import { OnClickableLink } from 'components/LinkComponents.js'
import SignUpBanner from 'components/SignUpBanner'
import useDigitalData from 'hooks/useDigitalData'

const Container = styled.div`
  text-align: center;
`

const Action = styled.button`
  color: ${black};
  text-decoration: underline;
  font-weight: ${fontWeights.medium};
`

const Heading = styled(H2)`
  text-transform: uppercase;
  font-family: 'Trim Poster';
`

const LogInModal = ({
  visible,
  close = () => {},
  values,
  errors,
  touched,
  handleSubmit,
  handleChange,
  handleBlur,
  isSubmitting,
  loginError,
  setSignupModalVisibility,
  resetForm
}) => {
  const { digitalData } = useDigitalData()

  const signup = () => {
    close()
    setSignupModalVisibility(true)
  }

  useEffect(() => {
    if (!visible) {
      untrackQueryParam('login_popup')
      return
    }

    trackQueryParam('login_popup')

    window.dataObject.time = Date()
    trackEvent({
      name: 'modalLogin',
      time: Date()
    })
  }, [visible])

  useEffect(() => {
    return () => {
      resetForm()
    }
  }, [resetForm])

  useEffect(() => {
    digitalData.push({
      page: {
        virtualUrl: location.pathname + '/login-modal',
        timestamp: new Date().getTime()
      }
    })
  }, [])

  return (
    <Modal visible={visible} close={close} wide hasWhiteClose>
      <Container>
        <SignUpBanner logoOnly />
        <ModalSection>
          <Heading>Log in</Heading>
          <p>
            Need to create an account? <Action onClick={signup}>Sign up</Action>.
          </p>
        </ModalSection>
        <ModalSection>
          <form onSubmit={handleSubmit}>
            <Fieldset>
              <FieldsetItem xs="1-1" sm="1-2">
                <Input
                  label="Email address"
                  name="email"
                  placeholder="john.doe@mail.com"
                  type="text"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  meta={{
                    error: errors.email,
                    touched: touched.email
                  }}
                />
              </FieldsetItem>

              <FieldsetItem xs="1-1" sm="1-2">
                <Input
                  label="Password"
                  name="password"
                  placeholder="••••••••••"
                  type="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  meta={{
                    error: errors.password,
                    touched: touched.password
                  }}
                  addon={
                    <Link to="/forgot-password/">
                      <OnClickableLink onClick={close} label="Forgot?" to="/forgot-password/" />
                    </Link>
                  }
                />
              </FieldsetItem>

              {loginError.length > 0 && (
                <FieldsetItem xs="1-1">
                  <FormFeedback type="error">Email or password incorrect</FormFeedback>
                </FieldsetItem>
              )}

              <FieldsetItem xs="1-1">
                <Button
                  theme="custom"
                  customColor={black}
                  size="large"
                  variant="outline"
                  type="submit"
                  disabled={isSubmitting}
                >
                  Log in
                </Button>
              </FieldsetItem>
            </Fieldset>
          </form>
        </ModalSection>
      </Container>
    </Modal>
  )
}

LogInModal.propTypes = {
  close: func,
  visible: bool,
  handleSubmit: func.isRequired,
  handleChange: func.isRequired,
  handleBlur: func.isRequired,
  values: object.isRequired,
  errors: object.isRequired,
  touched: object.isRequired,
  isSubmitting: bool.isRequired,
  handleUserLogin: func.isRequired,
  resetForm: func.isRequired,
  loginError: string,
  setSignupModalVisibility: func.isRequired
}

const handleSubmit = async (values, { setSubmitting, props }) => {
  setSubmitting(true)
  await props.handleUserLogin(values)
  setSubmitting(false)
}

const mapPropsToValues = () => ({
  email: '',
  password: ''
})

const validationSchema = () => {
  return Yup.lazy(() => {
    return Yup.object().shape({
      email: Yup.string().email('Email address is invalid').required('Email address is required'),
      password: Yup.string()
        .required('Password is required')
        .test('password', 'Must be at least 8 characters', minLength(8))
    })
  })
}

export default withFormik({
  mapPropsToValues,
  validationSchema,
  handleSubmit,
  validateOnChange: false
})(LogInModal)
